import { env } from '@/shared/lib/env';
import { useAuthStore } from '@/shared/auth/store';

/**
 * API client.
 *
 * - Auth is the `ism_portal` session cookie. The request carries no token: the
 *   edge validates the cookie (`auth_request` -> IdP `/internal/portal-resolve`)
 *   and injects the backend `Token` header itself, so it never exists in JS.
 * - JSON request/response.
 * - On 401 simply clears the local session and lets routing redirect to /login.
 *
 * Requests must therefore be same-origin — the proxy in front of the SPA owns
 * `/api`. Pointing `VITE_API_BASE_URL` at another origin still works for
 * unauthenticated calls only: a cross-origin cookie needs
 * `Access-Control-Allow-Credentials`, which the legacy API's hand-written
 * allow-list does not grant.
 *
 * For legacy form-encoded SP_Common-style endpoints, see legacy.ts.
 */

export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type RequestOptions = RequestInit & {
  /** Treat 401 as a normal outcome instead of ending the session. */
  anonymous?: boolean;
  /** Override response decoding. Default: parse JSON if any body. */
  parse?: 'json' | 'text' | 'blob' | 'none';
};

export function buildUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const base = env.apiBase.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export async function api<T = unknown>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const { anonymous, parse = 'json', headers, ...rest } = opts;

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has('Content-Type') && rest.body && typeof rest.body === 'string') {
    finalHeaders.set('Content-Type', 'application/json');
  }
  finalHeaders.set('Accept', 'application/json');

  const res = await fetch(buildUrl(path), {
    ...rest,
    credentials: 'include',
    headers: finalHeaders,
  });

  if (res.status === 401 && !anonymous) {
    useAuthStore.getState().clear();
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      try {
        body = await res.text();
      } catch {
        /* ignore */
      }
    }
    const message =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : null) ?? `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  if (parse === 'none' || res.status === 204) return undefined as T;
  if (parse === 'blob') return (await res.blob()) as T;
  if (parse === 'text') return (await res.text()) as T;

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
